import React, { useEffect, useState } from 'react';
import { Collapse, Empty, Image, Space, Spin, Typography } from 'antd';
import { shipmentApi, type ShipmentWeightConfirmationDTO } from '../api/kfc';

const fmtTime = (v?: string) => (v ? v.replace('T', ' ').slice(0, 19) : '-');
const kg = (v?: number | null) => (v != null ? `${v} kg` : '-');

const EvidenceImage: React.FC<{ shipmentId: string; recordId: string; index: number; fileName: string }> = ({
  shipmentId,
  recordId,
  index,
  fileName,
}) => {
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    let objectUrl: string | undefined;
    let cancelled = false;
    shipmentApi
      .getWeightConfirmationImage(shipmentId, recordId, index)
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setUrl(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [shipmentId, recordId, index]);
  if (!url) {
    return (
      <div style={{ width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
        <Spin size="small" />
      </div>
    );
  }
  return <Image src={url} alt={fileName} width={64} height={64} style={{ objectFit: 'cover', borderRadius: 4 }} />;
};

const RecordItem: React.FC<{ record: ShipmentWeightConfirmationDTO }> = ({ record }) => (
  <div style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: 12 }}>
    <Space wrap size={12} style={{ marginBottom: 4 }}>
      <Typography.Text strong>{fmtTime(record.confirmedAt)}</Typography.Text>
      <span>{record.confirmedByName || record.confirmedBy || '-'}</span>
      <span>
        确认重 <Typography.Text strong>{kg(record.confirmedWeight)}</Typography.Text>
        {record.beforeConfirmedWeight != null && `（原 ${kg(record.beforeConfirmedWeight)}）`}
      </span>
      {record.suggestedKg != null && <span>当时建议 {kg(record.suggestedKg)}</span>}
      {record.billingRuleName && <Typography.Text type="secondary">规则：{record.billingRuleName}</Typography.Text>}
    </Space>
    {record.evidenceText ? (
      <Typography.Paragraph
        style={{ whiteSpace: 'pre-wrap', background: '#fafafa', padding: 8, margin: '4px 0', fontSize: 12 }}
        ellipsis={{ rows: 3, expandable: true, symbol: '展开' }}
      >
        {record.evidenceText}
      </Typography.Paragraph>
    ) : null}
    {!!record.images?.length && (
      <Image.PreviewGroup>
        <Space wrap style={{ marginTop: 4 }}>
          {record.images.map((img, i) => (
            <EvidenceImage key={i} shipmentId={record.shipmentId} recordId={record.id} index={i} fileName={img.fileName} />
          ))}
        </Space>
      </Image.PreviewGroup>
    )}
    {!record.evidenceText && !record.images?.length && <Typography.Text type="secondary">未提供凭证</Typography.Text>}
  </div>
);

/** 确认重量记录：按时间倒序；refreshKey 变化时重新加载 */
const WeightConfirmationHistory: React.FC<{ shipmentId: string; refreshKey?: number }> = ({ shipmentId, refreshKey }) => {
  const [records, setRecords] = useState<ShipmentWeightConfirmationDTO[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    shipmentApi
      .listWeightConfirmations(shipmentId)
      .then((res) => {
        if (!cancelled) setRecords(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setRecords([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [shipmentId, refreshKey]);

  return (
    <Collapse
      size="small"
      style={{ marginTop: 12 }}
      items={[
        {
          key: 'history',
          label: `确认记录（${records.length}）`,
          children: (
            <Spin spinning={loading}>
              {records.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="暂无确认记录（上线前的确认可在操作日志查看）"
                />
              ) : (
                records.map((r) => <RecordItem key={r.id} record={r} />)
              )}
            </Spin>
          ),
        },
      ]}
    />
  );
};

export default WeightConfirmationHistory;
